import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Quote, Layers, Shield, MessageSquare, Headphones } from "lucide-react";
import ScenarioAnimation from "../components/driving/ScenarioAnimation";

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_HINT = {
  1: "Level 1 — 3 foundational competencies",
  2: "Level 2 — 6 communication & coaching",
  3: "Level 3 — 10 scaling & mechanics",
  4: "Level 4 — all 14 in a realistic flow",
};

export default function PdiScenario() {
  const { scenarioId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [level, setLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async (lvl = level) => {
    setLoading(true);
    try {
      const { data: payload } = await axios.get(`${API}/api/pdi-mode/scenarios/${scenarioId}?level=${lvl}`, { withCredentials: true });
      setData(payload.scenario);
      setProgress(payload.progress);
      if (payload.progress?.notes != null) setNotes(payload.progress.notes);
      if (payload.progress?.level_unlocked) setLevel(payload.progress.level_unlocked);
    } catch {
      toast.error("Couldn't load builder.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(1); }, [scenarioId]);

  const changeLevel = async (newLevel) => {
    setLevel(newLevel);
    await load(newLevel);
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/pdi-mode/scenarios/${scenarioId}/notes`,
        { notes, level_unlocked: level }, { withCredentials: true });
      toast.success("Saved your coaching notes.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-950"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  const visibleComps = data.competencies_visible || [];

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white pb-28">
      <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="ps-back" onClick={() => nav("/pdi-hub")} className="p-2 rounded-xl hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-400">Lesson Builder</p>
            <p className="text-sm font-extrabold truncate">{data.title}</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {/* Animation */}
        <ScenarioAnimation animationKey={data.animation_key} />

        {/* Level slider */}
        <section data-testid="ps-level-slider" className="rounded-2xl bg-slate-900 border border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-2 text-slate-400">
            <Layers className="w-4 h-4" />
            <p className="text-[11px] font-extrabold uppercase tracking-wider">Progression level</p>
          </div>
          <div className="grid grid-cols-4 gap-1 mb-2">
            {[1, 2, 3, 4].map((n) => (
              <button
                key={n}
                data-testid={`ps-level-${n}`}
                onClick={() => changeLevel(n)}
                className={`py-1.5 text-[11px] font-extrabold rounded-lg ${
                  level === n ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                }`}
              >L{n}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">{LEVEL_HINT[level]}</p>
        </section>

        {/* Competencies for current level */}
        <Section title="DVSA competencies in play" icon={Shield} color="emerald">
          {visibleComps.length === 0 ? (
            <p className="text-[12px] text-slate-400">None tagged yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {visibleComps.map((c) => (
                <span
                  key={c.key}
                  data-testid={`ps-comp-${c.key}`}
                  className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-900/40 text-emerald-200 border border-emerald-800"
                >
                  L{c.level} · {c.name}
                </span>
              ))}
            </div>
          )}
        </Section>

        {/* Coaching phrases */}
        <Section title="Coaching phrases" icon={Quote} color="violet">
          <Bullets items={data.pdi_coaching_phrases} testid="ps-phrases" />
        </Section>

        {/* Lesson structure */}
        <Section title="Lesson structure" icon={Layers} color="indigo">
          {data.pdi_lesson_structure?.length ? (
            <ol className="space-y-1.5 list-decimal list-inside">
              {data.pdi_lesson_structure.map((s, i) => (
                <li key={i} className="text-[12px] text-slate-300 leading-snug">{s}</li>
              ))}
            </ol>
          ) : <p className="text-[12px] text-slate-400">No structure yet.</p>}
        </Section>

        {/* Risk opportunities */}
        <Section title="Risk management opportunities" icon={Shield} color="rose">
          <Bullets items={data.pdi_risk_opportunities} testid="ps-risks" />
        </Section>

        {/* Questions to ask pupil */}
        <Section title="Questions to ask the pupil" icon={MessageSquare} color="indigo">
          <Bullets items={data.questions_to_ask} testid="ps-questions" />
        </Section>

        {/* Reflection prompts */}
        <Section title="Reflection prompts" icon={MessageSquare} color="emerald">
          <Bullets items={data.reflection_questions} testid="ps-reflections" />
        </Section>

        {/* Audio script */}
        {data.audio_script && (
          <Section title="Audio guidance script" icon={Headphones} color="amber">
            <p data-testid="ps-script" className="text-[12px] text-slate-300 italic leading-relaxed">"{data.audio_script}"</p>
          </Section>
        )}

        {/* Notes */}
        <Section title="My coaching notes" icon={Save} color="slate">
          <textarea
            data-testid="ps-notes-input"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 4000))}
            placeholder="Adaptations for nervous pupils, common mistakes, your own scripts…"
            className="w-full px-3 py-2 text-sm rounded-xl border border-slate-700 bg-slate-950 text-white resize-none"
          />
          <button
            data-testid="ps-notes-save"
            onClick={saveNotes}
            disabled={saving}
            className="mt-2 w-full px-3 py-2.5 text-xs font-extrabold rounded-xl bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-60 inline-flex items-center justify-center gap-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save notes & lock-in level
          </button>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, color = "slate", children }) {
  const tones = {
    slate:   "text-slate-400",
    emerald: "text-emerald-400",
    violet:  "text-violet-400",
    indigo:  "text-indigo-400",
    rose:    "text-rose-400",
    amber:   "text-amber-400",
  };
  return (
    <section className="rounded-2xl bg-slate-900 border border-slate-700 p-4">
      <div className={`flex items-center gap-2 mb-2 ${tones[color]}`}>
        <Icon className="w-4 h-4" />
        <p className="text-[11px] font-extrabold uppercase tracking-wider">{title}</p>
      </div>
      {children}
    </section>
  );
}

function Bullets({ items, testid }) {
  if (!items || items.length === 0) return <p className="text-[12px] text-slate-400">Nothing here yet.</p>;
  return (
    <ul data-testid={testid} className="space-y-1">
      {items.map((x, i) => (
        <li key={i} className="text-[12px] text-slate-300 leading-snug">• {x}</li>
      ))}
    </ul>
  );
}
